// views/Dashboard/PainelExecutivo/sections/marketing/SecaoMarketing.tsx
import React from 'react';
import { DollarSign, Eye, MousePointerClick, Target, TrendingUp, MessageCircle, Users } from 'lucide-react';
import type { FiltrosDashboard } from '../../types';
import { rotuloPeriodoDashboard } from '../../../../../src/lib/dashboard/periodoDashboard';
import { useMarketingDashboardData } from './useMarketingDashboardData';
import LoadingSpinner from '../../../../../src/components/LoadingSpinner';
import AnaliseMetricasChart from '../../../../Marketing/charts/AnaliseMetricasChart';
import ResultadosPorDiaChart from '../../../../Marketing/charts/ResultadosPorDiaChart';
import ComparativoResumo from '../../components/ComparativoResumo';

interface Props {
  filtros: FiltrosDashboard;
}

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v: number) => v.toLocaleString('pt-BR');

const CardMarketing: React.FC<{ icon: React.ElementType; tint: string; titulo: string; valor: string }> = ({ icon: Icon, tint, titulo, valor }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex items-start gap-3">
    <div className={`p-2.5 rounded-xl shrink-0 ${tint}`}><Icon size={18} /></div>
    <div className="min-w-0 flex-1">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate">{titulo}</p>
      <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5 truncate">{valor}</p>
    </div>
  </div>
);

const SecaoMarketing: React.FC<Props> = ({ filtros }) => {
  const { byDay, kpis, loading, erro } = useMarketingDashboardData(filtros.periodo);
  const { kpis: kpisComparacao } = useMarketingDashboardData(filtros.periodoComparacao);

  if (erro) {
    return <p className="text-sm font-semibold text-red-600 dark:text-red-400">{erro}</p>;
  }

  if (loading && byDay.length === 0) {
    return <LoadingSpinner label="Carregando seção Marketing..." />;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-bold text-gray-800 dark:text-white">Marketing</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        <CardMarketing icon={DollarSign} tint="bg-primary/10 text-primary" titulo="Investimento em Mídia Paga" valor={fmtBRL(kpis.totalSpend)} />
        <CardMarketing icon={Eye} tint="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400" titulo="Impressões" valor={fmtNum(kpis.totalImpressions)} />
        <CardMarketing icon={MousePointerClick} tint="bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400" titulo="Cliques no Link" valor={fmtNum(kpis.totalLinkClicks)} />
        <CardMarketing icon={Target} tint="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" titulo="Resultados" valor={fmtNum(kpis.totalResults)} />
        <CardMarketing icon={TrendingUp} tint="bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400" titulo="Custo por Resultado" valor={kpis.avgCostPerResult > 0 ? fmtBRL(kpis.avgCostPerResult) : '—'} />
        <CardMarketing icon={MessageCircle} tint="bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400" titulo="Conversas Iniciadas" valor={fmtNum(kpis.totalConversations)} />
        <CardMarketing icon={Users} tint="bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400" titulo="Leads / Cadastros" valor={fmtNum(kpis.totalLeads)} />
      </div>

      {filtros.periodoComparacao && (
        <ComparativoResumo
          rotuloComparacao={rotuloPeriodoDashboard(filtros.periodoComparacao)}
          itens={[
            { titulo: 'Investimento', valorAtual: kpis.totalSpend, valorComparacao: kpisComparacao.totalSpend, formatar: fmtBRL },
            { titulo: 'Impressões', valorAtual: kpis.totalImpressions, valorComparacao: kpisComparacao.totalImpressions, formatar: fmtNum },
            { titulo: 'Resultados', valorAtual: kpis.totalResults, valorComparacao: kpisComparacao.totalResults, formatar: fmtNum },
            { titulo: 'Leads / Cadastros', valorAtual: kpis.totalLeads, valorComparacao: kpisComparacao.totalLeads, formatar: fmtNum },
          ]}
        />
      )}

      <AnaliseMetricasChart byDay={byDay} />
      <ResultadosPorDiaChart byDay={byDay} kpis={kpis} />
    </div>
  );
};

export default SecaoMarketing;
